import {
  Customer,
  GeoLocationCoords,
  getStateCoords,
  State
} from 'ui-smartforce-settings';
import { ProjectArea } from '../../../../../../../../Models/ProjectValue';

const TILE_SIZE = 256;
const STATE_ZOOM = 5;
const ADDRESS_ZOOM = 14;

// Copy from StaticMap Component
const IMG_WIDTH = 640;
const IMG_HEIGHT = 640;
const SCALE = 2;

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const latRad = (lat: number): number => {
  const sin = Math.sin((lat * Math.PI) / 180);
  return Math.log((1 + sin) / (1 - sin)) / 2;
};

interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

const getBoundsFromAreas = (areas: GeoLocationCoords[][]): Bounds | null => {
  if (!areas?.length) return null;

  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;

  areas.forEach((area) => {
    area.forEach(({ latitude, longitude }) => {
      minLat = Math.min(minLat, latitude);
      maxLat = Math.max(maxLat, latitude);
      minLng = Math.min(minLng, longitude);
      maxLng = Math.max(maxLng, longitude);
    });
  });

  return { minLat, maxLat, minLng, maxLng };
};

const padBounds = (bounds: Bounds, paddingFactor = 0.1): Bounds => {
  const latSpan = bounds.maxLat - bounds.minLat;
  const lngSpan = bounds.maxLng - bounds.minLng;

  const latPadding = latSpan * paddingFactor;
  const lngPadding = lngSpan * paddingFactor;

  return {
    minLat: bounds.minLat - latPadding,
    maxLat: bounds.maxLat + latPadding,
    minLng: bounds.minLng - lngPadding,
    maxLng: bounds.maxLng + lngPadding
  };
};

const getCenterFromBounds = (bounds: Bounds): GeoLocationCoords => ({
  latitude: (bounds.minLat + bounds.maxLat) / 2,
  longitude: (bounds.minLng + bounds.maxLng) / 2
});

const getZoomForBounds = (
  bounds: Bounds,
  widthPx: number,
  heightPx: number,
  scale = 1,
  minZoom = 0,
  maxZoom = 21
): number => {
  const width = widthPx * scale;
  const height = heightPx * scale;

  const latFraction = (latRad(bounds.maxLat) - latRad(bounds.minLat)) / Math.PI;

  const lngDiff = bounds.maxLng - bounds.minLng;
  const lngFraction = ((lngDiff + 360) % 360) / 360;

  const latZoom = Math.log2(height / TILE_SIZE / Math.max(latFraction, 1e-9));
  const lngZoom = Math.log2(width / TILE_SIZE / Math.max(lngFraction, 1e-9));

  const zoom = Math.min(latZoom, lngZoom);
  return clamp(Math.floor(zoom), minZoom, maxZoom);
};

export const computeFitViewport = (
  areas: GeoLocationCoords[][],
  width = IMG_WIDTH,
  height = IMG_HEIGHT,
  scale = SCALE
) => {
  const bounds = getBoundsFromAreas(areas);
  if (!bounds) return null;

  const padded = padBounds(bounds, 0.15); // 15% extra margin

  // PROJECTS v4.4 | This value could be fit better
  const maxZoom = areas.length === 1 ? 13 : 12;

  return {
    center: getCenterFromBounds(padded),
    zoom: getZoomForBounds(padded, width, height, scale, 8, maxZoom)
  };
};

export const getLocationText = (
  customer: Customer | undefined,
  areas: ProjectArea[] | undefined
): string => {
  if (areas && areas.length > 0)
    return areas.map((area) => area.name).join(', ');

  if (customer && customer.address && customer.address?.full)
    return customer.address.full;

  return customer ? `${customer.state_name} State` : '';
};

export const getCenterPosition = (
  statesList: State[],
  customer: Customer
): GeoLocationCoords => {
  if (customer && customer.address && customer.address.coords)
    return customer.address.coords as GeoLocationCoords;

  return getStateCoords(statesList, customer.state_name);
};

export const getZoom = (customer: Customer | undefined): number => {
  if (customer && customer.address && customer.address.coords)
    return ADDRESS_ZOOM;

  return STATE_ZOOM;
};
